import { useEffect, useMemo, useState } from 'react';
import css from './RegionBalance.module.css';

interface RegionRow {
  region: string;
  sets: number;
  sessions: number;
}

interface Props {
  // Window is controlled by the parent (Dashboard) so it stays in sync
  // with the muscle-activation diagram below it. Parent fetches by days.
  days: number;
  fetchFor: (days: number) => Promise<{ regions: RegionRow[] } | null>;
}

const REGION_COLORS: Record<string, string> = {
  Back: '#3b82f6',
  Chest: '#ef4444',
  Legs: '#22c55e',
  Arms: '#a855f7',
  Shoulders: '#f59e0b',
  Buttocks: '#ec4899',
  Abdomen: '#06b6d4',
  Cardio: '#f97316',
  Stretching: '#94a3b8',
  '(unknown)': '#555',
};

export function RegionBalance({ days, fetchFor }: Props) {
  const [data, setData] = useState<RegionRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    fetchFor(days).then((r) => {
      if (!cancelled) setData(r?.regions ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [days, fetchFor]);

  const total = useMemo(() => data?.reduce((acc, r) => acc + r.sets, 0) ?? 0, [data]);

  if (!data) return <div className={css.loading}>…</div>;
  if (data.length === 0) return <div className={css.empty}>no sets in this window</div>;

  return (
    <ul className={css.list}>
      {data.map((r) => {
        const pct = total > 0 ? (r.sets / total) * 100 : 0;
        return (
          <li key={r.region} className={css.row}>
            <div className={css.label}>
              <span>{r.region}</span>
              <span className={css.count}>
                {r.sets.toLocaleString()} sets · {pct.toFixed(0)}%
              </span>
            </div>
            <div className={css.barTrack}>
              <div
                className={css.barFill}
                style={{
                  width: `${pct}%`,
                  background: REGION_COLORS[r.region] ?? '#666',
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
